import React from 'react';
import { motion } from 'framer-motion';

const FloatingElements: React.FC = () => {
  // Geometric Shapes - Squares, Triangles, Circles
  const geometricShapes = [...Array(6)].map((_, i) => ({
    type: i % 3, // 0=circle, 1=square, 2=triangle
    color: i % 2 === 0 ? 'rgba(192, 0, 53, 0.25)' : 'rgba(248, 204, 5, 0.25)',
    size: 30 + i * 12,
    left: `${10 + i * 15}%`,
    top: `${15 + i * 12}%`,
    duration: 10 + i * 2,
    delay: i * 0.5,
  }));

  // Gold Particles
  const particles = [...Array(12)].map(() => ({
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    duration: 4 + Math.random() * 3,
    delay: Math.random() * 3,
  }));

  // Pulsing Rings
  const pulsingRings = [...Array(2)].map((_, i) => ({
    left: `${40 + i * 25}%`,
    top: `${50 + i * 15}%`,
    size: 60 + i * 30,
    duration: 3 + i,
    delay: i * 0.5,
  }));

  // Light Orbs
  const lightOrbs = [...Array(3)].map((_, i) => ({
    left: `${25 + i * 25}%`,
    top: `${30 + i * 20}%`,
    size: 80 + i * 20,
    duration: 6 + i * 2,
    delay: i * 0.8,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
      {/* Geometric Shapes */}
      {geometricShapes.map((shape, i) => (
        <motion.div
          key={`shape-${i}`}
          className="absolute"
          style={{
            left: shape.left,
            top: shape.top,
            width: shape.size,
            height: shape.size,
            border: `2px solid ${shape.color}`,
            borderRadius: shape.type === 0 ? '50%' : shape.type === 1 ? '20%' : '0%',
            transform: shape.type === 2 ? 'rotate(45deg)' : 'none',
          }}
          animate={{
            rotate: [0, 360],
            scale: [0.8, 1.3, 0.8],
            opacity: [0.2, 0.8, 0.2],
            y: [0, -30, 0],
          }}
          transition={{
            duration: shape.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: shape.delay,
          }}
        />
      ))}

      {/* Gold Particles */}
      {particles.map((particle, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-1 h-1 bg-haat-gold rounded-full"
          style={{
            left: particle.left,
            top: particle.top,
          }}
          animate={{
            y: [0, -150, 0],
            opacity: [0, 1, 0],
            scale: [0, 1.5, 0],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Pulsing Rings */}
      {pulsingRings.map((ring, i) => (
        <motion.div
          key={`ring-${i}`}
          className="absolute border-2 border-haat-red/20 rounded-full"
          style={{
            left: ring.left,
            top: ring.top,
            width: ring.size,
            height: ring.size,
          }}
          animate={{
            scale: [0.5, 2, 0.5],
            opacity: [0.8, 0, 0.8],
          }}
          transition={{
            duration: ring.duration,
            repeat: Infinity,
            delay: ring.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Light Orbs */}
      {lightOrbs.map((orb, i) => (
        <motion.div
          key={`orb-${i}`}
          className="absolute rounded-full"
          style={{
            left: orb.left,
            top: orb.top,
            width: orb.size,
            height: orb.size,
            background: 'radial-gradient(circle, rgba(248, 204, 5, 0.3), transparent)',
          }}
          animate={{
            scale: [0.8, 1.3, 0.8],
            opacity: [0.3, 0.8, 0.3],
          }}
          transition={{
            duration: orb.duration,
            repeat: Infinity,
            delay: orb.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Floating Triangles */}
      {[...Array(4)].map((_, i) => (
        <motion.div
          key={`triangle-${i}`}
          className="absolute"
          style={{
            left: `${20 + i * 20}%`,
            top: `${30 + i * 20}%`,
            width: 0,
            height: 0,
            borderLeft: '15px solid transparent',
            borderRight: '15px solid transparent',
            borderBottom: '25px solid rgba(248, 204, 5, 0.25)',
          }}
          animate={{
            rotate: [0, 360],
            opacity: [0.2, 0.7, 0.2],
            scale: [0.5, 1.2, 0.5],
            y: [0, -40, 0],
          }}
          transition={{
            duration: 8 + i * 2,
            repeat: Infinity,
            delay: i * 1.5,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Floating Lines */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={`line-${i}`}
          className="absolute h-px bg-gradient-to-r from-transparent via-haat-gold/40 to-transparent"
          style={{
            left: `${15 + i * 25}%`,
            top: `${20 + i * 25}%`,
            width: `${100 + i * 30}px`,
          }}
          animate={{
            opacity: [0, 1, 0],
            scaleX: [0, 1, 0],
            x: [0, 50, 0],
          }}
          transition={{
            duration: 6 + i * 2,
            repeat: Infinity,
            delay: i * 2,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Parallax Light Accent */}
      <motion.div
        className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(248, 204, 5, 0.15), transparent)',
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </div>
  );
};

export default FloatingElements;
